import {
  Alert,
  Button,
  ButtonProps,
  CardHeader,
  keyframes,
  useTheme,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { isUndefined } from 'lodash';
import moment from 'moment';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import Confetti from './confetti-component';

// Animation keyframes
const slideInBounce = keyframes`
  0% {
    transform: translateX(100%) scale(0.8);
    opacity: 0;
  }
  60% {
    transform: translateX(-10px) scale(1.05);
    opacity: 0.9;
  }
  80% {
    transform: translateX(5px) scale(0.98);
    opacity: 1;
  }
  100% {
    transform: translateX(0) scale(1);
    opacity: 1;
  }
`;

const slideOutFade = keyframes`
  0% {
    transform: translateX(0) scale(1);
    opacity: 1;
  }
  100% {
    transform: translateX(100%) scale(0.8);
    opacity: 0;
  }
`;

export interface Toast {
  id?: string | number;
  title: string;
  body: ReactNode;
  // @deprecated use severity instead
  bg?: string;
  severity?: 'success' | 'error' | 'warning' | 'info';
  delay?: number;
  autohide?: boolean;
  action?: ButtonProps & { label: string };
  showConfetti?: boolean; // Flag to enable confetti animation for this specific toast
  removeOnNextPush?: boolean; // Flag to remove existing toast when a new toast is pushed, this is useful for toasts that represent transient states OR when if the generic network error toast should be superseded by a more specific one
}

export default function ToastrRenderer({
  toast,
  onRemove = () => {},
}: {
  toast: Toast;
  onRemove?: (id: string | number) => void;
}) {
  const [open, setOpen] = useState(true);
  const [showConfetti, setShowConfetti] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const theme = useTheme();

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const remainingTimeRef = useRef<number | null>(null);

  const severity = useMemo(() => {
    if (toast.severity) {
      return toast.severity;
    }
    // Fallback to bg for backwards compatibility
    switch (toast.bg) {
      case 'success':
        return 'success';
      case 'danger':
        return 'error';
      case 'warning':
        return 'warning';
      case 'info':
      default:
        return 'info';
    }
  }, [toast.bg, toast.severity]);

  const icon = useMemo(() => {
    switch (severity) {
      case 'success':
        return <CheckIcon />;
      case 'error':
        return <ErrorIcon />;
      case 'warning':
        return <WarningIcon />;
      case 'info':
      default:
        return <InfoIcon />;
    }
  }, [severity]);

  const handleClose = () => {
    setOpen(false);
    setShowConfetti(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (toast.id) {
      setTimeout(() => onRemove(toast.id!), 500); // Increased delay for better animation
    }
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      // Calculate remaining time
      if (startTimeRef.current !== null) {
        const elapsed = Date.now() - startTimeRef.current;
        const totalDelay = !isUndefined(toast.delay) ? toast.delay : 3000;
        remainingTimeRef.current = Math.max(0, totalDelay - elapsed);
      }
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    // Restart timer with remaining time
    if (remainingTimeRef.current !== null && remainingTimeRef.current > 0) {
      startTimeRef.current = Date.now();
      timeoutRef.current = setTimeout(() => {
        setOpen(false);
        setShowConfetti(false);
        if (toast.id) {
          setTimeout(() => onRemove(toast.id!), 500);
        }
      }, remainingTimeRef.current);
    }
  };

  // Trigger confetti only for toasts that explicitly request it
  useEffect(() => {
    if (open && toast.showConfetti) {
      setShowConfetti(true);
      // Stop confetti after animation duration
      const confettiTimer = setTimeout(() => {
        setShowConfetti(false);
      }, 3000);
      return () => clearTimeout(confettiTimer);
    }
    return undefined;
  }, [severity, open, toast.showConfetti, toast.id]);

  // Auto-hide functionality with hover pause support
  useEffect(() => {
    if (open && (!isUndefined(toast.delay) ? toast.delay : 3000) > 0) {
      const delay = !isUndefined(toast.delay) ? toast.delay : 3000;
      startTimeRef.current = Date.now();
      remainingTimeRef.current = delay;

      timeoutRef.current = setTimeout(() => {
        setOpen(false);
        setShowConfetti(false);
        if (toast.id) {
          setTimeout(() => onRemove(toast.id!), 500);
        }
      }, delay);

      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }
    return undefined;
  }, [open, toast.delay, toast.id, onRemove]);

  return (
    <>
      <Confetti show={showConfetti && toast.showConfetti} />

      <Alert
        onClose={handleClose}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        severity={severity}
        variant="filled"
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: '400px',
          minWidth: '300px',
          boxShadow: open
            ? '0 8px 32px rgba(0,0,0,0.2)'
            : '0 4px 16px rgba(0,0,0,0.1)',
          animation: open
            ? `${slideInBounce} 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)`
            : `${slideOutFade} 0.3s ease-out forwards`,
          transformOrigin: 'center right',
          '& .MuiAlert-message': {
            width: '100%',
          },
          // Add a subtle scale effect on hover
          '&:hover': {
            transform: 'scale(1.02)',
            transition: 'transform 0.2s ease-out',
          },
          // Padding adjustments to accommodate action button
          pb: toast.action ? 6 : 2,
          // Add visual feedback when hovered (paused)
          ...(isHovered && {
            boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
          }),
        }}
      >
        <CardHeader
          avatar={icon}
          sx={{ p: 0, pb: 1 }}
          title={toast.title}
          subheader={moment().format('LT')}
          slotProps={{
            title: { fontSize: '0.9rem', fontWeight: 'bold' },
            subheader: { fontSize: '0.75rem', opacity: 0.8 },
          }}
        />

        {toast.body}

        {toast.action && (
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            sx={{
              position: 'absolute',
              bottom: theme.spacing(1),
              left: theme.spacing(1),
              right: theme.spacing(1),
            }}
            {...toast.action}
          >
            {toast.action.label}
          </Button>
        )}
      </Alert>
    </>
  );
}
